import {
  CreateNineSetupForm,
  UpdateNineSetupPayload,
  UpdateNineSetupEnquiryPayload,
} from "../../../interfaces/NineSetupObject";

/**
 * UI → wire payloads for the nine-setup-controller endpoints.
 *
 * Field names here match NineSetupCreateRq / NineSetupUpdateRq in the User
 * Service OpenAPI spec (https://ninefinds.com/api/v1/user/v3/api-docs).
 * Note `mediaRequired` on the wire vs. `isMediaRequired` in the UI row type —
 * that rename is applied in both directions and only here + nineSetupConvertor.
 *
 * Two rules govern every builder:
 *
 * 1. `undefined` keys are DROPPED, `null` keys are KEPT. On an update this is
 *    the whole contract: `undefined` means "leave this column alone", `null`
 *    means "clear it". A naive JSON.stringify already drops undefined, but the
 *    server receives the object we build — so the distinction is enforced
 *    explicitly rather than relied upon as a side effect.
 *
 * 2. Server-owned columns are never sent: the lifecycle `*At` stamps (derived
 *    from the status transition — client clocks would let skew reorder the
 *    funnel), `createdAt`/`updatedAt` (the DB trigger owns updatedAt), and
 *    `captainId` (derived from the bearer token, exactly like
 *    create-pending-business). The primary key IS sent on update — the endpoint
 *    is a POST to a fixed path with `id` in the body, not a PUT to `/{id}`.
 *
 * The type system does most of the enforcing: both builders are keyed off
 * EditableNineSetupFields, so a server-owned column simply has nowhere to go.
 */

/** Copies `source`, omitting keys whose value is `undefined`. Preserves `null`
 *  (see rule 1). Typed to return a partial of the input so callers keep their
 *  key names checked. */
function omitUndefined<T extends object>(source: T): Partial<T> {
  const result: Partial<T> = {};
  (Object.keys(source) as (keyof T)[]).forEach((key) => {
    if (source[key] !== undefined) {
      result[key] = source[key];
    }
  });
  return result;
}

/** Trims a string field, mapping a blank result to undefined so the key is
 *  dropped rather than sent as "". Pass-through for null (an explicit clear). */
function trimmedOrUndefined(value: string | null | undefined): string | null | undefined {
  if (value === undefined || value === null) return value;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * CreateNineSetupForm → `POST /api/v1/nine-setup/create-nine-setup` body
 * (NineSetupCreateRq).
 *
 * `businessName` and `customerMobile` are always sent (the two NOT NULL columns
 * a client supplies). Everything else is optional and omitted when unset, which
 * lets the DB defaults stand — notably `mediaRequired` (TRUE). Sending an
 * explicit value would make the UI the source of truth for a default it
 * doesn't own.
 *
 * NOT SENT, because NineSetupCreateRq has no field for them: `status`,
 * `statusNote`, `curatorId`, `businessId`. A row is always born at the default
 * status and acquires the rest through update-nine-setup. `CreateNineSetupForm`
 * still types them (it is a Partial of the editable fields) — if a create form
 * ever needs to seed one, it takes a follow-up update, not a wider body.
 *
 * `customerMobile` is UNIQUE table-wide, so a duplicate here is an expected
 * server rejection, not a validation bug — see DUPLICATE_CUSTOMER_MOBILE in
 * NineSetupApiService.
 */
export function buildCreateNineSetupPayload(form: CreateNineSetupForm) {
  return omitUndefined({
    // Required columns — trimmed, but never dropped: an empty value must reach
    // the server and fail its NOT NULL check loudly rather than vanish here.
    businessName: form.businessName.trim(),
    customerMobile: form.customerMobile.trim(),

    // Business
    location: trimmedOrUndefined(form.location),
    latitude: form.latitude,
    longitude: form.longitude,

    // Captain (captainId intentionally absent — bearer token identifies them)
    captainMobile: trimmedOrUndefined(form.captainMobile),

    // Intake — wire name is `mediaRequired`, UI name is `isMediaRequired`.
    googleFormLink: trimmedOrUndefined(form.googleFormLink),
    mediaRequired: form.isMediaRequired,

    // Money
    onboardingFee: form.onboardingFee,
    curatorFee: form.curatorFee,
  });
}

/**
 * UpdateNineSetupPayload → `POST /api/v1/nine-setup/update-nine-setup` body
 * (NineSetupUpdateRq).
 *
 * `setupId` becomes `id` IN THE BODY — this endpoint is a POST to a fixed path,
 * so the PK has nowhere else to travel. It is the one server-owned column the
 * client does send.
 *
 * Because unset keys are dropped, a status-only transition produces exactly
 * `{ id, status, statusNote }` — the server stamps the matching `*At` column and
 * the `trg_set_updated_at_nine_setup` trigger handles `updated_at`.
 */
export function buildUpdateNineSetupPayload(payload: UpdateNineSetupPayload) {
  const { setupId, ...fields } = payload;

  return omitUndefined({
    // Addresses the row. Always sent — an update without it has no target.
    id: setupId,

    // These two are NOT NULL columns, so they cannot be cleared — `?? undefined`
    // deliberately collapses an explicit null into "leave alone" rather than
    // sending a null the server would reject. That's the opposite of `location`
    // and the fields below it, where null IS how you empty the column.
    businessName: trimmedOrUndefined(fields.businessName) ?? undefined,
    customerMobile: trimmedOrUndefined(fields.customerMobile) ?? undefined,

    // Nullable columns: undefined leaves them alone, null clears them, and a
    // blank string is dropped rather than guessed at.
    location: trimmedOrUndefined(fields.location),
    latitude: fields.latitude,
    longitude: fields.longitude,

    captainMobile: trimmedOrUndefined(fields.captainMobile),

    // Wire name is `mediaRequired`, UI name is `isMediaRequired`.
    googleFormLink: trimmedOrUndefined(fields.googleFormLink),
    mediaRequired: fields.isMediaRequired,

    status: fields.status,
    statusNote: trimmedOrUndefined(fields.statusNote),

    curatorId: fields.curatorId,
    businessId: fields.businessId,

    onboardingFee: fields.onboardingFee,
    curatorFee: fields.curatorFee,

    imageCount: fields.imageCount,
    pdfCount: fields.pdfCount,
    pdfTotalPages: fields.pdfTotalPages,

    targetDate: fields.targetDate,
  });
}


export function buildUpdateNineSetupEnquiryPayload(payload: UpdateNineSetupEnquiryPayload) {
  return {
    id: payload.setupId,
    enquiryNote: trimmedOrUndefined(payload.enquiryNote),
    enquiryTargetDate: payload.enquiryTargetDate || null,
  };
}

export default {
  buildCreateNineSetupPayload,
  buildUpdateNineSetupPayload,
  buildUpdateNineSetupEnquiryPayload,
};
